import * as yup from "yup";
import { formatDate } from "../../UI/Utils/formatDate.ts";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { InputWithLabel } from "../../UI/InputWithLabel.tsx";
import { TextInput } from "../../UI/TextInput.tsx";
import { ClassRoomPlanSelect } from "./ClassRoomPlanSelect.tsx";
import { PrimaryButton } from "../../UI/PrimaryButton.tsx";
import { useWithLoading } from "../../UI/Loading/useWithLoading.ts";
import { ClassRoomPlanWarrantyConfigDTO } from "./ClassRoomPlanWarrantyConfigDTO.tsx";
import { ClassRoomPlanWarrantyConfigInput } from "./ClassRoomPlanWarrantyConfigInput.tsx";
import { HorizontalLineDivider } from "../../UI/HorizontalLineDivider.tsx";
import { Col } from "../../UI/Layout/Col.tsx";
import { Row } from "../../UI/Layout/Row.tsx";
import { Select } from "@normasteaching/norma-ui";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { isDefined } from "../../isDefined.ts";
import { convertToInt } from "../../DataUtils/convertToInt.ts";
import { SpinnerContainer } from "../../UI/Loading/SpinnerContainer.tsx";
import { useCalculatePlanGivenSlot } from "./useCalculatePlanGivenSlot.ts";
import { PlanBonusesEditor } from "./PlanBonusesEditor.tsx";
import { ManualBonusFormInput, formatBonus } from "./planBonusUtils.ts";
import {
  getStartOfWeek,
  getEndOfWeek,
  computeEndAtWeekFromStartAndWeeks,
} from "./planWeekDateUtils.ts";

export type UpdateUserClassRoomPlanFormInputs = {
  startAtWeek: Date;
  endAtWeek: Date;
  weeks: number;
  lessonsPerWeek: number;
  planToRecoverId?: string | null;
  warrantyConfig?: ClassRoomPlanWarrantyConfigDTO;
  unlimitedLessonsPerWeek?: {
    enabled: boolean;
    totalLessons?: number;
  };
  bonuses: ManualBonusFormInput[];
};

enum YesNo {
  Yes = "Yes",
  No = "No",
}

const schema = yup
  .object({
    startAtWeek: yup
      .date()
      .typeError("Devi specificare una data valida")
      .required("Inserisci una data di inizio"),
    endAtWeek: yup
      .date()
      .typeError("Devi specificare una data valida")
      .required("Inserisci una data di fine"),
    weeks: yup
      .number()
      .typeError("Devi specificare un numero")
      .integer()
      .min(1, "Minimo 1 settimana")
      .required("Inserisci la durata in settimane"),
    lessonsPerWeek: yup
      .number()
      .typeError("Devi specificare un numero")
      .integer()
      .required("Inserisci il numero di lezioni a settimana"),
    planToRecoverId: yup.string().optional().nullable(),
    warrantyConfig: yup
      .object()
      .shape({
        enabled: yup.boolean().optional(),
        minAttendedLessons: yup.number().when("enabled", {
          is: (val: boolean) => val,
          then: (yup) => yup.required(),
        }),
      })
      .optional(),
    unlimitedLessonsPerWeek: yup
      .object({
        enabled: yup.boolean().required(),
        totalLessons: yup.number().when("enabled", {
          is: (v: unknown) => !!v,
          then: (yup) =>
            yup
              .required("Inserire il numero totale di lezioni")
              .min(1, "Deve essere almeno 1"),
        }),
      })
      .optional(),
    bonuses: yup.array().optional().default([]),
  })
  .required();

export function UpdateUserClassRoomPlanForm({
  userId,
  onSave,
  defaultValues,
  planId,
}: {
  userId: string;
  onSave: (data: UpdateUserClassRoomPlanFormInputs) => Promise<void>;
  defaultValues?: Partial<UpdateUserClassRoomPlanFormInputs>;
  planId: string;
}) {
  const [bonuses, setBonuses] = useState<ManualBonusFormInput[]>(
    defaultValues?.bonuses ?? [],
  );
  const { withLoading, loading: updating } = useWithLoading();
  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
    setValue,
    control,
  } = useForm<UpdateUserClassRoomPlanFormInputs>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const planToRecoverId = watch("planToRecoverId");
  const startDate = watch("startAtWeek");
  const endDate = watch("endAtWeek");
  const weeks = watch("weeks");
  const lessonsPerWeek = watch("lessonsPerWeek");
  const unlimitedLessonsPerWeekEnabled =
    watch("unlimitedLessonsPerWeek.enabled") ?? false;
  const totalLessons = watch("unlimitedLessonsPerWeek.totalLessons");

  useEffect(() => {
    if (!isDefined(startDate) || !dayjs(startDate).isValid()) return;
    if (!isDefined(weeks) || isNaN(Number(weeks)) || Number(weeks) < 1) return;
    setValue(
      "endAtWeek",
      computeEndAtWeekFromStartAndWeeks(startDate, Number(weeks)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, weeks]);

  const { calculatedGivenSlot, loading: loadingGivenSlotCount } =
    useCalculatePlanGivenSlot({
      startedAtWeekUtc:
        isDefined(startDate) && dayjs(startDate).isValid()
          ? getStartOfWeek(startDate).toISOString()
          : undefined,
      endedAtWeekUtc:
        isDefined(endDate) && dayjs(endDate).isValid()
          ? getEndOfWeek(endDate).toISOString()
          : undefined,
      lessonsPerWeek: isDefined(lessonsPerWeek)
        ? convertToInt(lessonsPerWeek)
        : undefined,
      unlimitedLessonPerWeek: {
        enabled: unlimitedLessonsPerWeekEnabled,
        totalLessons: isDefined(totalLessons)
          ? convertToInt(totalLessons)
          : undefined,
      },
    });

  function mapData(
    data: UpdateUserClassRoomPlanFormInputs,
  ): UpdateUserClassRoomPlanFormInputs {
    return {
      ...data,
      startAtWeek: getStartOfWeek(data.startAtWeek).toDate(),
      endAtWeek: getEndOfWeek(data.endAtWeek).toDate(),
      bonuses,
    };
  }

  const onSubmit: SubmitHandler<UpdateUserClassRoomPlanFormInputs> = async (
    data,
  ) => {
    await withLoading(async () => onSave(mapData(data)));
  };

  return (
    <Col>
      <Row>
        <InputWithLabel label="Start date">
          <Controller
            control={control}
            render={({ field }) => (
              <TextInput
                errorMessage={errors.startAtWeek?.message}
                onChange={(e) => field.onChange(e)}
                value={
                  field.value ? dayjs(field.value).format("YYYY-MM-DD") : ""
                }
                type={"date"}
              />
            )}
            name={"startAtWeek"}
          />
        </InputWithLabel>
        <InputWithLabel label="Weeks">
          <TextInput
            errorMessage={errors.weeks?.message}
            {...register("weeks")}
            type={"number"}
          />
        </InputWithLabel>
      </Row>
      {unlimitedLessonsPerWeekEnabled ? null : (
        <Row>
          <InputWithLabel label="Weekly lessons count">
            <TextInput
              errorMessage={errors.lessonsPerWeek?.message}
              {...register("lessonsPerWeek")}
              type={"number"}
            />
          </InputWithLabel>
        </Row>
      )}
      <Row>
        <div className={"flex flex-col space-y-4 w-full"}>
          <div className={"w-full"}>
            <div className={"flex items-center space-x-4"}>
              <HorizontalLineDivider />
              <p className={"whitespace-nowrap font-bold"}>
                Unlimited lessons per week
              </p>
              <HorizontalLineDivider />
            </div>
          </div>

          <div className={"flex flex-col space-y-4"}>
            <InputWithLabel label={"Enabled"}>
              <Controller
                render={({ field }) => {
                  return (
                    <Select<YesNo>
                      value={field.value ? YesNo.Yes : YesNo.No}
                      onChange={(v) =>
                        v === YesNo.Yes
                          ? field.onChange(true)
                          : field.onChange(false)
                      }
                      options={[
                        { id: YesNo.No, name: "No" },
                        { id: YesNo.Yes, name: "Yes" },
                      ]}
                      errorMessage={
                        errors?.unlimitedLessonsPerWeek?.enabled?.message
                      }
                    />
                  );
                }}
                name={"unlimitedLessonsPerWeek.enabled"}
                control={control}
              />
            </InputWithLabel>
            {!unlimitedLessonsPerWeekEnabled ? null : (
              <InputWithLabel label={"Total lessons"}>
                <TextInput
                  {...register("unlimitedLessonsPerWeek.totalLessons")}
                  errorMessage={
                    errors?.unlimitedLessonsPerWeek?.totalLessons?.message
                  }
                  type={"number"}
                />
              </InputWithLabel>
            )}
          </div>
        </div>
      </Row>
      {isDefined(startDate) &&
        isDefined(endDate) &&
        dayjs(startDate).isValid() &&
        dayjs(endDate).isValid() && (
          <Row>
            <SpinnerContainer loading={loadingGivenSlotCount}>
              <div className={"bg-blue-200 rounded-md p-3 w-full"}>
                <p>
                  {`The plan will go from the week starting ${formatDate(getStartOfWeek(startDate), { withWeekday: true })} to the week ending ${formatDate(getEndOfWeek(endDate), { withWeekday: true })}${isDefined(weeks) ? ` (${weeks} weeks)` : ""}`}
                </p>
                {calculatedGivenSlot ? (
                  <p>{`In total, the user will be able to book ${calculatedGivenSlot} hours of lessons.`}</p>
                ) : null}
                {bonuses.length > 0 ? (
                  <p>{`Bonuses applied: ${bonuses.map(formatBonus).join(", ")}`}</p>
                ) : null}
              </div>
            </SpinnerContainer>
          </Row>
        )}
      <InputWithLabel label={"Plan to recover"}>
        <Controller
          render={({ field }) => (
            <ClassRoomPlanSelect
              selectedPlanId={field.value ?? undefined}
              onChange={field.onChange}
              filterPlans={(plans) =>
                plans.filter((p) => p.id !== planId && !p.planToRecoverId)
              }
              userId={userId}
            />
          )}
          name={"planToRecoverId"}
          control={control}
        />
      </InputWithLabel>
      {planToRecoverId ? null : (
        <div>
          <div className={"flex items-center space-x-4"}>
            <HorizontalLineDivider />
            <p className={"whitespace-nowrap font-bold"}>
              Warranty Configuration
            </p>
            <HorizontalLineDivider />
          </div>
          <Controller
            render={({ field }) => (
              <ClassRoomPlanWarrantyConfigInput
                warrantyConfig={field.value}
                onChange={field.onChange}
              />
            )}
            name={"warrantyConfig"}
            control={control}
          />
        </div>
      )}
      <Row>
        <PlanBonusesEditor bonuses={bonuses} onChange={setBonuses} />
      </Row>
      <div className={"flex justify-end"}>
        <PrimaryButton
          onClick={handleSubmit(onSubmit)}
          loading={updating}
          label={"Update plan"}
        />
      </div>
    </Col>
  );
}
